<script>
import { Line } from 'vue-chartjs'
import Tools from '@/tools/tools.js'

const brandInfo = '#4dbd74'
const convertHex = (hex, opacity) => {
  hex = hex.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  return `rgba(${r},${g},${b},${opacity / 100})`
}

const normDay = v => {
  const d = new Date(v)
  if (isNaN(d)) return String(v)
  return d.toISOString().slice(0, 10)
}
const fmtBR = v => {
  const d = new Date(v)
  return isNaN(d) ? String(v) : d.toLocaleDateString('pt-BR')
}
const niceTop = v => {
  if (!isFinite(v) || v <= 0) return 10
  const p = Math.pow(10, Math.floor(Math.log10(v)))
  const n = Math.ceil(v / p)
  const step = [1, 2, 5, 10].find(s => n <= s) || 10
  return step * p
}

const fmtBRL = v =>
  Number(v || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

const tooltipLabel = (ti, data) => {
  const dsLabel = (data.datasets[ti.datasetIndex]?.label) || ''
  return `${dsLabel}: ${fmtBRL(ti.yLabel)}`
}
const yTicks = v => fmtBRL(v)

export default {
  extends: Line,
  name: 'GraficoLinhasVendasVendedor',
  props: {
    dt1: { type: String, default: '' },
    dt2: { type: String, default: '' },
    vendedorId: { type: [String, Number], default: '' }
  },
  data () {
    return {
      busy: false,
      datasetTipos: [],
      keysOrdenadas: [],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0, animateScale: false, animateRotate: false },
        hover: { animationDuration: 0 },
        responsiveAnimationDuration: 0,
        legend: { display: false },
        tooltips: { mode: 'index', intersect: false, callbacks: { label: tooltipLabel } },
        elements: {
          line: { tension: 0.3 },
          // aumenta a area de click
          point: { radius: 2, hitRadius: 12, hoverRadius: 4, hoverBorderWidth: 3 }
        },
        spanGaps: true,
        scales: {
          xAxes: [{ gridLines: { drawOnChartArea: false }, ticks: { maxRotation: 60, minRotation: 60, autoSkip: true, autoSkipPadding: 8 } }],
          yAxes: [{ gridLines: { display: true }, ticks: { beginAtZero: true, maxTicksLimit: 6, callback: yTicks } }]
        },
        plugins: { zoom: { pan: { enabled: false }, zoom: { enabled: false } } }
      },
      datasetsBase: [
        { label: 'Vendas', backgroundColor: convertHex(brandInfo, 10), borderColor: brandInfo, pointHoverBackgroundColor: '#fff', borderWidth: 2, data: [] }
      ]
    }
  },
  methods: {
    abrirModal (diaIso, tipo) {
      this.$emit('abrir-modal', { dia: diaIso, tipo })
    },

    renderGrafico (labels, valoresVendas) {
      const maxVal = Math.max(0, ...(valoresVendas || []))
      const yMax = niceTop(maxVal)

      // monta options preservando callbacks e travando eixo
      const options = {
        ...this.chartOptions,
        tooltips: { ...this.chartOptions.tooltips, mode: 'index', intersect: false, callbacks: { label: tooltipLabel } },
        scales: {
          ...this.chartOptions.scales,
          yAxes: [{
            ...this.chartOptions.scales.yAxes?.[0],
            ticks: { ...this.chartOptions.scales.yAxes?.[0]?.ticks, min: 0, max: yMax, callback: yTicks }
          }]
        }
      }

      // mapeamento dataset -> tipo
      this.datasetTipos = ['VENDA']

      // CLICK no ponto (robusto)
      options.onClick = (evt) => {
        if (!this._chart) return
        let elems = []
        if (typeof this._chart.getElementsAtEventForMode === 'function') {
          // Chart.js >= 2.7
          elems = this._chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true, axis: 'x' }, false)
        } else {
          // fallback
          elems = this._chart.getElementAtEvent(evt) || []
        }
        if (elems && elems.length) {
          const el = elems[0]
          const dsIdx = el._datasetIndex
          const idx = el._index
          const diaIso = this.keysOrdenadas[idx]
          const tipo = this.datasetTipos[dsIdx] || 'VENDA'
          if (diaIso) this.abrirModal(diaIso, tipo)
        }
      }

      // cursor pointer no hover
      options.onHover = (evt, active) => {
        const el = evt?.target
        if (el && el.style) el.style.cursor = active && active.length ? 'pointer' : 'default'
      }

      const datasets = [
        { ...this.datasetsBase[0], data: [...valoresVendas] }
      ]
      const data = { labels: [...labels], datasets }

      if (this._chart) {
        this._chart.destroy()
        const canvas = this.$el
        if (canvas && canvas.getContext) {
          const ctx = canvas.getContext('2d')
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
      }

      this.$nextTick(() => { this.renderChart(data, options) })
    },

    async getDados () {
      this.$emit('busy', true)
      try {
        this.busy = true
        if (!this.vendedorId) {
          this.keysOrdenadas = []
          this.renderGrafico([], [])
          return
        }

        const { data: payload } = await this.$http.get('/api/protected/venda/getVendasPorDiaVendedorId', {
          params: { dt1: this.dt1, dt2: this.dt2, vendedorId: this.vendedorId }
        })
        const itens = Array.isArray(payload) ? payload : (payload?.data || [])

        const mapVendas = Object.create(null)
        for (const it of itens) {
          const dia = normDay(it.DT_SAIDA || it.dt_saida || it.dtSaida)
          const valor = Number(it.TOTAL_VENDAS ?? it.TOTAL_VENDAS ?? it.vlTotalNfe ?? it.total ?? 0)
          if (dia) mapVendas[dia] = (mapVendas[dia] || 0) + valor
        }

        const allKeys = Object.keys(mapVendas).sort((a, b) => a.localeCompare(b))
        this.keysOrdenadas = allKeys

        const valoresVendas = allKeys.map(k => mapVendas[k] || 0)
        const labels = allKeys.map(fmtBR)

        this.renderGrafico(labels, valoresVendas)
      } catch (e) {
        Tools.exibirMensagem(this.$store, 'Nao foi possivel buscar os dados de vendas do vendedor', 'danger', e)
      } finally {
        this.busy = false
        // avisa o pai que terminou (com sucesso ou erro)
        this.$emit('busy', false)
      }
    }
  },
  watch: {
    dt1: 'getDados',
    dt2: 'getDados',
    vendedorId: 'getDados'
  },
  async created () {
    await this.getDados()
  },
  beforeDestroy () {
    if (this._chart) this._chart.destroy()
  }
}
</script>



// WEBPACK FOOTER //
// src/views/dashboard/GraficoLinhasVendasVendedor.vue